import { getApiBaseUrl } from './api/client';
import { isWebSocketEvent } from '@/types/websocket';
import { useSessionStore } from '@/store/sessionStore';
import { useMcpStore } from '@/store/mcpStore';
import { toast } from 'sonner';
import { SessionInput } from '@/types/api';

let ws: WebSocket | null = null;
let reconnectInterval: ReturnType<typeof setInterval> | null = null;
let retries = 0;
const maxRetries = 10;
const reconnectDelay = 5000;
let currentWsUrl: string | null = null;

// Debounce timer for refresh-ui events
let refreshUiTimer: ReturnType<typeof setTimeout> | null = null;

const findLatestModelResponse = (sessionInputs: SessionInput[]): SessionInput | null => {
  const modelInputs = sessionInputs.filter(input => input.role === 'model');
  if (modelInputs.length === 0) return null;
  const sortedBySequence = [...modelInputs].sort((a, b) =>
    (b.sequence_number || 0) - (a.sequence_number || 0)
  );
  for (const input of sortedBySequence) {
    if (input.raw_llm_response !== null) return input;
  }
  return null;
};

function handleMessage(event: MessageEvent) {
  try {
    const parsedData = JSON.parse(event.data);
    if (!isWebSocketEvent(parsedData)) return;

    const message = parsedData;

    switch (message.event) {
      case 'plan-ready': {
        const { sessionId } = message.data;
        if (useSessionStore.getState().currentSessionId === sessionId) {
          (async () => {
            await useSessionStore.getState().fetchSessionDetails(sessionId, 'refresh');
            const { autoScrollEnabled, setActiveMainPage } = useSessionStore.getState();
            if (autoScrollEnabled) {
              setActiveMainPage('plan');
            }
          })();
        }
        break;
      }
      case 'refresh-ui': {
        if (refreshUiTimer) clearTimeout(refreshUiTimer);
        refreshUiTimer = setTimeout(async () => {
          refreshUiTimer = null;
          useSessionStore.getState().clearStreamingResponse();
          useSessionStore.getState().clearStreamingThoughts();
          const { currentSessionId, fetchSessionDetails, fetchChildSessions, activePlanInput, autoScrollEnabled } =
            useSessionStore.getState();

          if (currentSessionId) {
            await fetchSessionDetails(currentSessionId, 'refresh');
            fetchChildSessions(currentSessionId);
            const { currentSessionDetails, setActivePlanInputData } = useSessionStore.getState();
            if (currentSessionDetails?.sessionInputs) {
              const latestModelInput = findLatestModelResponse(currentSessionDetails.sessionInputs);
              if (latestModelInput) {
                const shouldUpdateData = !activePlanInput?.id ||
                  activePlanInput.id !== latestModelInput.id;
                if (shouldUpdateData && autoScrollEnabled) {
                  setActivePlanInputData(latestModelInput);
                }
              }
            }
          }
        }, 150);
        break;
      }
      case 'llm-stream-chunk': {
        const { sessionInputId, sessionId, chunk, isFirst } = message.data;
        const { currentSessionId, startStreamingResponse, appendStreamingResponse } = useSessionStore.getState();
        // Only process stream chunks for the currently viewed session
        if (sessionId && sessionId !== currentSessionId) break;
        if (isFirst) {
          startStreamingResponse({ sessionInputId, content: chunk });
        } else {
          appendStreamingResponse(chunk);
        }
        break;
      }
      case 'llm-thought-chunk': {
        const { sessionInputId, sessionId, chunk, isFirst } = message.data;
        const { currentSessionId, startStreamingThoughts, appendStreamingThoughts } = useSessionStore.getState();
        // Only process thought chunks for the currently viewed session
        if (sessionId && sessionId !== currentSessionId) break;
        if (isFirst) {
          startStreamingThoughts({ sessionInputId, content: chunk });
        } else {
          appendStreamingThoughts(chunk);
        }
        break;
      }
      case 'llm-generation-started': {
        const { sessionInputId, sessionId, isStreaming } = message.data;
        const { setLlmGenerationStarted, setIsRunning, updateSessionIsRunning, currentSessionId, fetchChildSessions } = useSessionStore.getState();
        updateSessionIsRunning(sessionId, true);
        if (sessionId === currentSessionId) {
          setLlmGenerationStarted(sessionInputId, sessionId, isStreaming);
          setIsRunning(true);
        } else {
          // Event from a child/sub-agent session — refresh child sessions
          if (currentSessionId) {
            fetchChildSessions(currentSessionId);
          }
        }
        break;
      }
      case 'llm-generation-in-progress': {
        const { sessionInputId } = message.data;
        const { setLlmGenerationInProgress } = useSessionStore.getState();
        setLlmGenerationInProgress(sessionInputId);
        break;
      }
      case 'llm-generation-ended': {
        const { sessionInputId, sessionId, error } = message.data;
        const { setLlmGenerationEnded, clearStreamingThoughts, clearStreamingResponse, resetLlmGenerationStatus, setIsRunning, updateSessionIsRunning, currentSessionId, fetchChildSessions } = useSessionStore.getState();
        updateSessionIsRunning(sessionId, false);
        if (sessionId === currentSessionId) {
          setLlmGenerationEnded(sessionInputId, error);
          // Clear all leftover streaming state now that generation has ended, so
          // the streaming UI hides immediately rather than waiting for the
          // debounced `refresh-ui` event (which can be delayed in a throttled
          // background tab).
          clearStreamingResponse();
          clearStreamingThoughts();
          resetLlmGenerationStatus();
          setIsRunning(false);
        } else {
          // Event from a child/sub-agent session — refresh child sessions
          if (currentSessionId) {
            fetchChildSessions(currentSessionId);
          }
        }
        break;
      }
      case 'session-created': {
        const { sessionId } = message.data;
        toast.success('New session created.');
        const { fetchSessions, setCurrentSessionId, setActiveMainPage } = useSessionStore.getState();
        fetchSessions().then(() => {
          setCurrentSessionId(sessionId);
          setActiveMainPage('plan');
        });
        break;
      }
      case 'system-prompt-update':
        break;
      case 'mcp-marketplace-connected': {
        const { marketplaceId, serverName } = message.data;
        useMcpStore.getState().handleMarketplaceConnected(marketplaceId, serverName);
        break;
      }
      case 'mcp-marketplace-error': {
        const { marketplaceId, error } = message.data;
        useMcpStore.getState().handleMarketplaceError(marketplaceId, error);
        break;
      }
      case 'mcp-token-expired': {
        const { serverName, marketplaceId } = message.data;
        useMcpStore.getState().handleTokenExpired(serverName, marketplaceId ?? null);
        break;
      }
      default:
        break;
    }
  } catch {
    // Silent error handling
  }
}

export function connectWebSocket(): void {
  // Don't reconnect if already connected to the same URL
  const apiUrl = new URL(getApiBaseUrl());
  const wsUrl = `ws://${apiUrl.hostname}:${apiUrl.port}`;

  if (currentWsUrl === wsUrl && ws && ws.readyState === WebSocket.OPEN) {
    return; // Already connected
  }

  // Disconnect existing connection first
  disconnectWebSocket();

  currentWsUrl = wsUrl;
  retries = 0;

  try {
    ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      toast.success('WebSocket connected.', { duration: 2000 });
      retries = 0;
      if (reconnectInterval) {
        clearInterval(reconnectInterval);
        reconnectInterval = null;
      }
      // Reconcile streaming state in case completion events were missed while
      // the socket was down (e.g. backgrounded/frozen tab).
      useSessionStore.getState().reconcileStreamingState();
    };

    ws.onmessage = handleMessage;

    ws.onclose = () => {
      ws = null;
      if (retries < maxRetries) {
        if (!reconnectInterval) {
          reconnectInterval = setInterval(attemptReconnect, reconnectDelay);
        }
        retries++;
      } else {
        toast.error('Could not reconnect to WebSocket.');
        if (reconnectInterval) {
          clearInterval(reconnectInterval);
          reconnectInterval = null;
        }
      }
    };

    ws.onerror = () => {
      ws?.close(); // Triggers onclose → reconnection logic
    };
  } catch {
    // Silent error handling
  }
}

function attemptReconnect(): void {
  if (!currentWsUrl) return;

  // If still connected or connecting, skip
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;

  try {
    ws = new WebSocket(currentWsUrl);

    ws.onopen = () => {
      toast.success('WebSocket reconnected.', { duration: 2000 });
      retries = 0;
      if (reconnectInterval) {
        clearInterval(reconnectInterval);
        reconnectInterval = null;
      }
      // Reconcile streaming state in case completion events were missed while
      // the socket was down (e.g. backgrounded/frozen tab).
      useSessionStore.getState().reconcileStreamingState();
    };

    ws.onmessage = handleMessage;

    ws.onclose = () => {
      ws = null;
      retries++;
      if (retries >= maxRetries) {
        toast.error('Could not reconnect to WebSocket.');
        if (reconnectInterval) {
          clearInterval(reconnectInterval);
          reconnectInterval = null;
        }
      }
    };

    ws.onerror = () => {
      ws?.close();
    };
  } catch {
    // Silent error handling
  }
}

export function disconnectWebSocket(): void {
  if (reconnectInterval) {
    clearInterval(reconnectInterval);
    reconnectInterval = null;
  }
  if (ws) {
    ws.onclose = null; // Prevent reconnection on manual close
    ws.close();
    ws = null;
  }
  currentWsUrl = null;
  if (refreshUiTimer) {
    clearTimeout(refreshUiTimer);
    refreshUiTimer = null;
  }
}

/**
 * Switch to a new backend port:
 * 1. Disconnect from current WebSocket
 * 2. Clear session/streaming state
 * 3. Update bport URL param
 * 4. Connect to new backend
 * 5. Load active session from new backend
 */
export function switchToPort(port: number): void {
  // 1. Disconnect from current backend
  disconnectWebSocket();

  // 2. Clear session state
  const store = useSessionStore.getState();
  store.clearStreamingResponse();
  store.clearStreamingThoughts();
  store.resetLlmGenerationStatus();
  store.setIsRunning(false);

  // 3. Update bport URL param
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('bport', String(port));
    window.history.replaceState({}, '', url.toString());
  } catch {
    // URL manipulation can fail in some environments; non-critical
  }

  // 4. Connect to new backend
  connectWebSocket();

  // 5. Notify AppInitializer that bport changed (so it reconnects WebSocket)
  window.dispatchEvent(new CustomEvent('bport-change'));

  // 6. Load active session from new backend
  store.loadActiveSession();
}